// Pure, dependency-free policy helpers for the query layer.
//
// Extracted from queryClient.js / referenceQueries.js specifically so they can
// be unit-tested without React, axios, or a DOM — see ./test/run-tests.mjs.
// Anything with a decision in it belongs here rather than inline in a hook.

/** Shared frozen empty array. A fresh [] per render would break memo deps. */
export const EMPTY = Object.freeze([]);

/**
 * Retry policy. modules/infrastructure/http/api-client.js ALREADY owns every
 * auth failure path: it refreshes on 401/410 and replays the original request,
 * and redirects on 403 MFA_REQUIRED. Retrying a 4xx here would duplicate that
 * work and, on the MFA path, fire redundant redirects. So: never retry a 4xx;
 * retry a 5xx or a transport error exactly once.
 *
 * @param {number} failureCount how many times this query has already failed
 * @param {{response?: {status?: number}}} error the rejection from axios
 * @returns {boolean} whether to try again
 */
export function shouldRetry(failureCount, error) {
  const status = error?.response?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return failureCount < 1;
}

/**
 * Normalise a reference-data payload to a plain array.
 *
 * Several Stride metadata endpoints answer with an ENVELOPE rather than a bare
 * array — /api/groups returns `{ header, total, groups }` and
 * /api/issue-link-types returns `{ issueLinkTypes }`. A naive
 * `Array.isArray(data) ? data : []` guard silently blanks those, which is a
 * failure mode that looks exactly like "the server returned nothing".
 *
 * @param {unknown} data raw response body
 * @param {string} [envelopeKey] property holding the array, when enveloped
 * @returns {ReadonlyArray<unknown>} the array, or EMPTY — never null/undefined
 */
export function unwrapList(data, envelopeKey) {
  if (envelopeKey && Array.isArray(data?.[envelopeKey])) return data[envelopeKey];
  if (Array.isArray(data)) return data;
  return EMPTY;
}

/** Methods that can change server state. GET/HEAD/OPTIONS never invalidate. */
const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

/**
 * URL prefixes whose mutations can change cached reference data.
 *
 * - /api/stride-cloner/cloned/ — the Migration console's admin data editors.
 *   Field, status, work-type-scheme and option CRUD all live under here
 *   (migration/server/routes.js:2571, :1242, :2146, :2753 …). Matched as one
 *   broad prefix rather than enumerated: new editors get covered for free, and
 *   over-invalidating is cheap (see the note on cost below).
 * - /api/admin/users, /api/admin/groups — the Users & Groups admin
 *   (auth-supabase/server/admin-routes.js:482, :1207 …), which backs
 *   useUsers / useGroups.
 * - /api/projects — project creation, which backs useProjects.
 */
const REFERENCE_WRITE_PREFIXES = [
  '/api/stride-cloner/cloned/',
  '/api/admin/users',
  '/api/admin/groups',
  '/api/projects',
];

/**
 * Should a successful request invalidate the cached reference data?
 *
 * Cost of a false positive is one lazy refetch — invalidateQueries only
 * REFETCHES currently-mounted queries and merely marks the rest stale. Cost of
 * a false negative is an admin staring at a list that is up to 10 minutes out
 * of date, which reads as a bug. So this deliberately errs toward invalidating.
 *
 * @param {string} method HTTP verb, any case
 * @param {string} url request URL, absolute or relative
 * @returns {boolean}
 */
export function affectsReferenceData(method, url) {
  if (!method || typeof url !== 'string') return false;
  if (!MUTATING.has(String(method).toLowerCase())) return false;

  // Normalise to a path so an absolute URL (API_BASE-prefixed) matches too.
  let path = url;
  const schemeAt = path.indexOf('://');
  if (schemeAt !== -1) {
    const slash = path.indexOf('/', schemeAt + 3);
    path = slash === -1 ? '/' : path.slice(slash);
  }
  path = path.split('?')[0].split('#')[0];

  // NOTE: /api/issue-link (a link BETWEEN two work items) must NOT match —
  // only /api/issue-link-types is reference data, and that endpoint is
  // read-only. Prefix matching on '/api/projects' likewise must not catch
  // '/api/project-issue-type-schemes', which is scheme wiring, not the
  // project list. Both are covered by tests.
  return REFERENCE_WRITE_PREFIXES.some(p => path === p || path.startsWith(
    p.endsWith('/') ? p : `${p}/`,
  ));
}
